import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { updateIndividualLedgerAction } from "../../../../actions/updateIndividualLedgerAction";
import { getUserData } from "../../../../utils/authnicate";
import "./index.scss";

/**
 * UpdateLedgerName Modal
 * Opens when the edit icon is clicked on a row in the Ledger Names table.
 * Displays:
 *  - Company Name (read-only / non-editable)
 *  - Ledger Name (editable text field)
 * Submits to: POST api/Onboarding/update-individual-ledger
 */
const UpdateLedgerName = ({
  isOpen,
  onClose,
  ledgerItem = null,
  entity = null,
  onSuccess,
}) => {
  const [ledgerName, setLedgerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const companyName =
    ledgerItem?.companyName ||
    ledgerItem?.entityName ||
    entity?.companyName ||
    entity?.entityName ||
    entity?.EntityName ||
    "—";

  const entityId = ledgerItem?.entityId || entity?.entityId || "";
  const gstNo = ledgerItem?.gstNo || "";

  useEffect(() => {
    if (isOpen) {
      setLedgerName(ledgerItem?.ledgerName || "");
      setError("");
    }
  }, [isOpen, ledgerItem]);

  const handleClose = () => {
    if (submitting) return;
    setLedgerName("");
    setError("");
    onClose();
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = ledgerName.trim();
    if (!trimmed) {
      setError("Ledger name is required");
      return;
    }

    setSubmitting(true);
    setError("");

    const loggedInUser = getUserData();
    const createdBy = loggedInUser?.email || loggedInUser?.name || "Admin";

    const payloadId =
      ledgerItem?.id !== undefined && ledgerItem?.id !== null
        ? Number(ledgerItem.id)
        : 0;

    const payloadEntityPrimaryId =
      ledgerItem?.entityPrimaryId || entity?.entityPrimaryId || entity?.id || 0;

    const result = await updateIndividualLedgerAction({
      id: payloadId,
      entityPrimaryId: Number(payloadEntityPrimaryId) || 0,
      entityId: entityId || "",
      ledgerName: trimmed,
      entityName: companyName !== "—" ? companyName : entity?.entityName || "",
      createdBy,
    });

    setSubmitting(false);

    if (result.success) {
      toast.success(result.message || "Ledger updated successfully");
      if (onSuccess) {
        onSuccess({
          ...ledgerItem,
          id: payloadId,
          entityPrimaryId: Number(payloadEntityPrimaryId) || 0,
          entityId,
          companyName,
          entityName: companyName,
          ledgerName: trimmed,
          oldLedgerName: ledgerItem?.ledgerName,
        });
      }
      handleClose();
    } else {
      setError(result.message || "Failed to update ledger");
      toast.error(result.message || "Failed to update ledger");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Ledger Name"
      size="small"
    >
      <form onSubmit={handleSave} className="uln-body">
        {/* Info badges */}
        {(entityId || gstNo) && (
          <div className="uln-entity-strip">
            {entityId && (
              <div className="uln-badge uln-badge--id">
                <span className="uln-badge__label">Entity ID:</span>
                <span className="uln-badge__value">{entityId}</span>
              </div>
            )}
            {gstNo && (
              <div className="uln-badge uln-badge--gst">
                <span className="uln-badge__label">GST No:</span>
                <span className="uln-badge__value">{gstNo}</span>
              </div>
            )}
          </div>
        )}

        {/* Company Name — read-only / non-editable */}
        <div className="uln-field">
          <label className="uln-field__label" htmlFor="uln-company-name">
            Company Name
            <span
              className="uln-field__lock-badge"
              title="Company Name cannot be edited"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Read-only
            </span>
          </label>
          <input
            id="uln-company-name"
            type="text"
            className="uln-field__input uln-field__input--disabled"
            value={companyName}
            disabled
            readOnly
          />
        </div>

        {/* Ledger Name — editable */}
        <div className="uln-field">
          <label className="uln-field__label" htmlFor="uln-ledger-name">
            Ledger Name <span className="uln-field__required">*</span>
          </label>
          <input
            id="uln-ledger-name"
            type="text"
            className={`uln-field__input${error ? " uln-field__input--error" : ""}`}
            placeholder="Enter ledger name"
            value={ledgerName}
            disabled={submitting}
            autoFocus
            onChange={(e) => {
              setLedgerName(e.target.value);
              if (error) setError("");
            }}
          />
          {error && <span className="uln-field__error">{error}</span>}
        </div>

        {/* Modal actions */}
        <div className="uln-actions">
          <button
            type="button"
            className="uln-btn uln-btn--cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="uln-btn uln-btn--submit"
            disabled={submitting || !ledgerName.trim()}
          >
            {submitting ? "Updating..." : "Update Ledger"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdateLedgerName;
